import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/App";
import { Bell, Check, CheckCheck, X, FileText, Gavel, Shield, Upload, Info } from "lucide-react";

const typeIcons = {
  court_case: Gavel,
  title_report: FileText,
  pio: Shield,
  document: Upload,
  info: Info,
};

const typeColors = {
  court_case: "#F59E0B",
  title_report: "#D4AF37",
  pio: "#556B2F",
  document: "#D4AF37",
  info: "#A1A1AA",
};

export default function NotificationPanel() {
  const { API } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const [nRes, cRes] = await Promise.all([
        fetch(`${API}/notifications`, { credentials: "include" }),
        fetch(`${API}/notifications/unread-count`, { credentials: "include" }),
      ]);
      if (nRes.ok) setNotifications(await nRes.json());
      if (cRes.ok) {
        const data = await cRes.json();
        setUnreadCount(data.count);
      }
    } catch (e) { console.error(e); }
  }, [API]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const markRead = async (id) => {
    await fetch(`${API}/notifications/${id}/read`, { method: "PUT", credentials: "include" });
    fetchNotifications();
  };

  const markAllRead = async () => {
    await fetch(`${API}/notifications/read-all`, { method: "PUT", credentials: "include" });
    fetchNotifications();
  };

  const timeAgo = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        data-testid="notifications-bell"
        onClick={() => setIsOpen(!isOpen)}
        className="w-8 h-8 flex items-center justify-center text-white/40 hover:text-white transition-colors relative"
      >
        <Bell className="w-4 h-4" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 bg-[#D4AF37] text-black text-[10px] font-sans font-bold rounded-full flex items-center justify-center px-1">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-10 w-80 bg-[#11130F] border border-white/10 rounded-sm shadow-2xl z-50 max-h-[400px] flex flex-col">
          <div className="flex items-center justify-between p-3 border-b border-white/10">
            <h4 className="font-sans font-medium text-white text-sm">Notifications</h4>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  data-testid="mark-all-read-btn"
                  onClick={markAllRead}
                  className="text-[#D4AF37] text-[10px] font-sans hover:underline flex items-center gap-1"
                >
                  <CheckCheck className="w-3 h-3" /> Mark all read
                </button>
              )}
              <button onClick={() => setIsOpen(false)} className="text-white/30 hover:text-white">
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center">
                <Bell className="w-8 h-8 text-white/10 mx-auto mb-2" />
                <p className="text-white/30 text-xs font-sans">No notifications yet</p>
              </div>
            ) : (
              notifications.map((n) => {
                const Icon = typeIcons[n.type] || Info;
                const color = typeColors[n.type] || "#A1A1AA";
                return (
                  <div
                    key={n.notification_id}
                    className={`flex items-start gap-3 p-3 border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer ${
                      !n.read ? "bg-[#D4AF37]/5" : ""
                    }`}
                    onClick={() => !n.read && markRead(n.notification_id)}
                  >
                    <div className="w-7 h-7 rounded-sm flex items-center justify-center shrink-0" style={{ backgroundColor: color + "15" }}>
                      <Icon className="w-3.5 h-3.5" style={{ color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-white text-xs font-sans font-medium truncate">{n.title}</p>
                        {!n.read && <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full shrink-0 ml-2" />}
                      </div>
                      <p className="text-white/50 text-[10px] font-sans mt-0.5 line-clamp-2">{n.message}</p>
                      <p className="text-white/30 text-[10px] font-sans mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
